import React, { useEffect, useRef, useMemo } from 'react';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chartjs-adapter-date-fns';
import { ChartData } from '@rti/shared';
import { getRsColors } from '@/utils/chartColors';
import { useTheme } from '@/contexts/ThemeContext';
import { normalizeSeries } from '@/utils/normalizeChartData';

Chart.register(...registerables, ChartDataLabels);

interface ForecastChartProps {
  chartData: ChartData | null;
}

const ForecastChart: React.FC<ForecastChartProps> = ({ chartData }) => {
  const { isDark } = useTheme();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const creatingRef = useRef(false);
  const hasAnimatedRef = useRef(false);

  const rsColors = useMemo(() => getRsColors(), []);
  // Axis/grid/tooltip colors must derive from the reactive `isDark` value, NOT from
  // getRsColors() (which reads CSS variables once at mount and goes stale on theme change).
  const tickColor = isDark ? '#e9ede6' : '#1e2019';
  const gridColor = isDark ? '#566151' : '#d0d3ce';
  const tooltipBg = isDark ? '#2a2e28' : '#f0f2ee';

  const fullFairSeries = useMemo(() => normalizeSeries(chartData?.fairPrice), [chartData]);
  const fullMidSeries = useMemo(() => normalizeSeries(chartData?.mid), [chartData]);
  const fullUpperSeries = useMemo(() => normalizeSeries(chartData?.upper), [chartData]);
  const fullLowerSeries = useMemo(() => normalizeSeries(chartData?.lower), [chartData]);
  const { fairSeries, midSeries, upperSeries, lowerSeries, lastFairDate } = useMemo(() => {
    if (!fullFairSeries.length) {
      return { fairSeries: [], midSeries: [], upperSeries: [], lowerSeries: [], lastFairDate: null };
    }

    const toUtcDate = (date: string | Date) => {
      const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      return new Date(dateStr + 'T00:00:00Z');
    };

    const sortedFair = [...fullFairSeries].sort((a: any, b: any) => {
      const dateA = toUtcDate(a.x);
      const dateB = toUtcDate(b.x);
      return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
    });
    const lastFairPoint = sortedFair[sortedFair.length - 1];
    const lastFairDate = toUtcDate(lastFairPoint.x);

    let startWindowDate: Date;
    if (lastFairDate.getUTCMonth() <= 4) {
      const targetMonth = lastFairDate.getUTCMonth() - 6;
      const targetYear = targetMonth < 0 ? lastFairDate.getUTCFullYear() - 1 : lastFairDate.getUTCFullYear();
      const adjustedMonth = targetMonth < 0 ? targetMonth + 12 : targetMonth;
      startWindowDate = new Date(Date.UTC(targetYear, adjustedMonth, 1));
    } else {
      startWindowDate = new Date(Date.UTC(lastFairDate.getUTCFullYear(), 0, 1));
    }

    const fairSeries = sortedFair.filter(p => {
      const pDate = toUtcDate(p.x);
      return pDate >= startWindowDate && pDate <= lastFairDate;
    });

    const futureFilter = (s: any[]) => s.filter(p => toUtcDate(p.x) > lastFairDate);
    let midFuture = futureFilter(fullMidSeries);
    let upperFuture = futureFilter(fullUpperSeries);
    let lowerFuture = futureFilter(fullLowerSeries);

    if (midFuture.length) midFuture = [{ x: lastFairPoint.x, y: lastFairPoint.y }, ...midFuture];
    if (upperFuture.length) upperFuture = [{ x: lastFairPoint.x, y: lastFairPoint.y }, ...upperFuture];
    if (lowerFuture.length) lowerFuture = [{ x: lastFairPoint.x, y: lastFairPoint.y }, ...lowerFuture];

    const allDatesSet = new Set<string>();
    fairSeries.forEach(p => allDatesSet.add(p.x));
    midFuture.forEach(p => allDatesSet.add(p.x));
    upperFuture.forEach(p => allDatesSet.add(p.x));
    lowerFuture.forEach(p => allDatesSet.add(p.x));

    const allDates = Array.from(allDatesSet).sort((a, b) => {
      const dateA = toUtcDate(a);
      const dateB = toUtcDate(b);
      return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
    });


    const createAlignedArray = (sourceData: any[], isHistorical: boolean) => {
      const dataMap = new Map();
      sourceData.forEach(p => dataMap.set(p.x, p.y));

      return allDates.map(date => {
        const dateObj = toUtcDate(date);
        const hasData = dataMap.has(date);
        const shouldShow = isHistorical ? dateObj <= lastFairDate : dateObj >= lastFairDate;

        return {
          x: date,
          y: hasData && shouldShow ? dataMap.get(date) : null
        };
      });
    };

    return {
      fairSeries: createAlignedArray(fairSeries, true),
      midSeries: createAlignedArray(midFuture, false),
      upperSeries: createAlignedArray(upperFuture, false),
      lowerSeries: createAlignedArray(lowerFuture, false),
      lastFairDate,
    };
  }, [fullFairSeries, fullMidSeries, fullUpperSeries, fullLowerSeries]);

  useEffect(() => {
    if (!chartRef.current || !(fairSeries.length || midSeries.length || upperSeries.length || lowerSeries.length)) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    if (creatingRef.current) return;
    creatingRef.current = true;

    if (chartInstance.current) {
      try { chartInstance.current.destroy(); } catch {  }
      chartInstance.current = null;
    }

    const datasets: any[] = [];

    if (fairSeries.length) {
      datasets.push({
        label: 'Fair Price',
        data: fairSeries.map(p => {
          const utcDate = new Date(p.x + 'T00:00:00Z');
          return { x: utcDate, y: p.y };
        }),
        borderColor: rsColors.darkGreen,
        backgroundColor: rsColors.darkGreen,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: (context: any) => {
          const point = context.dataset.data[context.dataIndex];
          return point && point.y === null ? 0 : 4;
        },
        pointHoverRadius: (context: any) => {
          const point = context.dataset.data[context.dataIndex];
          return point && point.y === null ? 0 : 6;
        },
        fill: false,
        spanGaps: false,
        datalabels: {
          display: (ctx: any) => {
            const pt = ctx.dataset.data[ctx.dataIndex];
            return pt && pt.y !== null && pt.y !== undefined;
          },
          anchor: (ctx: any) => ctx.dataIndex % 2 === 0 ? 'end' : 'start',
          align: (ctx: any) => ctx.dataIndex % 2 === 0 ? 'top' : 'bottom',
          offset: 8,
          color: rsColors.darkGreen,
          borderWidth: 1,
          borderRadius: 4,
          padding: 4,
          font: { size: 13.7, weight: 'bold' },
          formatter: (value: any) => {
            if (value.y === null || value.y === undefined) return '';
            return value.y.toFixed(2);
          },
          animation: {
            delay: (ctx: any) => ctx.dataIndex * 100 + 500
          }
        }
      });
    }

    if (upperSeries.length) {
      datasets.push({
        label: 'Upper Range',
        data: upperSeries.map(p => {
          const utcDate = new Date(p.x + 'T00:00:00Z');
          return { x: utcDate, y: p.y };
        }),
        borderColor: rsColors.blueDash,
        backgroundColor: 'rgba(54,162,235,0.15)',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: (context: any) => {
          const point = context.dataset.data[context.dataIndex];
          return point && point.y === null ? 0 : 4;
        },
        pointHoverRadius: (context: any) => {
          const point = context.dataset.data[context.dataIndex];
          return point && point.y === null ? 0 : 6;
        },
        fill: '+2',
        spanGaps: false,
        datalabels: { display: false }
      });
    }

    if (midSeries.length) {
      datasets.push({
        label: 'Forecast',
        data: midSeries.map(p => {
          const utcDate = new Date(p.x + 'T00:00:00Z');
          return { x: utcDate, y: p.y };
        }),
        borderColor: rsColors.salmon,
        backgroundColor: rsColors.salmon,
        borderDash: [6,6],
        tension: 0.4,
        borderWidth: 3,
        pointRadius: (context: any) => {
          const point = context.dataset.data[context.dataIndex];
          return point && point.y === null ? 0 : 4;
        },
        pointHoverRadius: (context: any) => {
          const point = context.dataset.data[context.dataIndex];
          return point && point.y === null ? 0 : 6;
        },
        fill: false,
        spanGaps: false,
        datalabels: {
          display: (ctx: any) => {
            const pt = ctx.dataset.data[ctx.dataIndex];
            if (pt && (pt.y === null || pt.y === undefined)) return false;

            if (!lastFairDate) return true;
            if (ctx.dataIndex === 0) return false;
            if (pt && pt.x) {
              const ptDate = new Date(pt.x);
              if (ptDate <= lastFairDate) return false;
            }
            return true;
          },
          anchor: (ctx: any) => ctx.dataIndex % 2 === 0 ? 'end' : 'start',
          align: (ctx: any) => ctx.dataIndex % 2 === 0 ? 'top' : 'bottom',
          offset: 8,
          color: rsColors.salmon,
          borderWidth: 1,
          borderRadius: 4,
          padding: 4,
          font: { size: 13.7, weight: 'bold' },
          formatter: (value: any) => {
            if (value.y === null || value.y === undefined) return '';
            return value.y.toFixed(2);
          },
          animation: {
            delay: (ctx: any) => ctx.dataIndex * 100 + 800
          }
        }
      });
    }

    if (lowerSeries.length) {
      datasets.push({
        label: 'Lower Range',
        data: lowerSeries.map(p => {
          const utcDate = new Date(p.x + 'T00:00:00Z');
          return { x: utcDate, y: p.y };
        }),
        borderColor: rsColors.plumDash || rsColors.purple,
        backgroundColor: 'rgba(133,76,255,0.15)',
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        spanGaps: false,
        datalabels: { display: false }
      });
    }

    const config: ChartConfiguration = {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            right: 15,
          }
        },
        parsing: {
          xAxisKey: 'x',
          yAxisKey: 'y'
        },
        animation: hasAnimatedRef.current ? false : {
          duration: 1200,
          easing: 'easeOutQuart',
        },
        animations: hasAnimatedRef.current ? {} : {
          x: {
            type: 'number',
            duration: 1200,
            easing: 'easeOutQuart',
            from: NaN,
            delay: (ctx: any) => {
              if (ctx.type !== 'data') return 0;
              
              const datasetIndex = ctx.datasetIndex || 0;
              return ctx.dataIndex * 35 + datasetIndex * 300;
            }
          },
          y: {
            type: 'number',
            duration: 1200,
            easing: 'easeOutCubic',
            from: (ctx: any) => {
              if (ctx.type !== 'data') return undefined;
              const yScale = ctx.chart.scales.y;
              return yScale.getPixelForValue ? yScale.min : undefined;
            },
            delay: (ctx: any) => {
              if (ctx.type !== 'data') return 0;
              const datasetIndex = ctx.datasetIndex || 0;
              return ctx.dataIndex * 35 + datasetIndex * 300;
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: tickColor,
              usePointStyle: true,
              pointStyle: 'line',
              font: { size: 13.7 },
            },
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: tooltipBg,
            titleColor: tickColor,
            bodyColor: tickColor,
            borderColor: tickColor,
            borderWidth: 1,
            usePointStyle: true,
            filter: (tooltipItem) => {
              
              const ts = tooltipItem.parsed.x;
              const tsDate = new Date(ts);

              
              if (tsDate.getFullYear() === 1900 && tsDate.getMonth() === 0 && tsDate.getDate() === 1) {
                return false;
              }

              
              if (tooltipItem.parsed.y === null || tooltipItem.parsed.y === undefined) {
                return false;
              }

              if (!lastFairDate) return true;

              const isFair = tooltipItem.dataset.label === 'Fair Price';
              const isForecast = tooltipItem.dataset.label !== 'Fair Price';

              
              
              return tsDate <= lastFairDate ? isFair : isForecast;
            },
            itemSort: (a, b) => {
              
              const datasetOrder = ['Fair Price', 'Forecast', 'Upper Range', 'Lower Range'];
              const aIndex = datasetOrder.indexOf(a.dataset.label || '');
              const bIndex = datasetOrder.indexOf(b.dataset.label || '');
              return aIndex - bIndex;
            },
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems.length) return '';
                const ts = tooltipItems[0].parsed.x;
                const tsDate = new Date(ts);

                
                if (tsDate.getFullYear() === 1900) return '';

                return tsDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone: 'UTC'
                });
              },
              label: (ctx) => {
                const y = ctx.parsed.y;
                return `${ctx.dataset.label}: ${y.toFixed(2)}`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: { month: 'MMM yy' }
            },
            grid: { color: gridColor },
            ticks: {
              color: tickColor,
              maxTicksLimit: 8,
              source: 'auto'
            },
            min: fairSeries.length > 0 ? new Date(fairSeries[0].x + 'T00:00:00Z').getTime() : undefined,
            max: (() => {
              const maxTime = Math.max(
                lowerSeries.length > 0 ? new Date(lowerSeries[lowerSeries.length - 1].x + 'T00:00:00Z').getTime() : 0,
                midSeries.length > 0 ? new Date(midSeries[midSeries.length - 1].x + 'T00:00:00Z').getTime() : 0,
                upperSeries.length > 0 ? new Date(upperSeries[upperSeries.length - 1].x + 'T00:00:00Z').getTime() : 0,
                fairSeries.length > 0 ? new Date(fairSeries[fairSeries.length - 1].x + 'T00:00:00Z').getTime() : 0
              );
              return maxTime > 0 ? maxTime + (10 * 24 * 60 * 60 * 1000) : undefined;
            })(),
          },
          y: {
            grid: { color: gridColor },
            ticks: {
              color: tickColor,
              callback: (v: any) => Number(v).toFixed(2)
            },
            grace: '.045'
          }
        },
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
      }
    };

    try {
      chartInstance.current = new Chart(ctx, config);
      if (!hasAnimatedRef.current) hasAnimatedRef.current = true;
    } catch (error) {
      console.error('❌ ForecastChart - Error creating chart:', error);
    }

    creatingRef.current = false;

    return () => {
      if (chartInstance.current) {
        try { chartInstance.current.destroy(); } catch {  }
        chartInstance.current = null;
      }
      creatingRef.current = false;
    };
  }, [fairSeries, midSeries, upperSeries, lowerSeries, rsColors, lastFairDate, isDark, tickColor, gridColor, tooltipBg]);

  return (fairSeries.length || midSeries.length || upperSeries.length || lowerSeries.length)
    ? <div className="w-full h-full relative">
        <canvas ref={chartRef} className="w-full h-full" />
      </div>
    : <div className="text-xs text-gray-400 p-2">No forecast data</div>;
};

export default ForecastChart;
